// Issue types for service-record corrections. Keys, roles and requirements
// mirror backend/unified_api/service_record_corrections.py ISSUE_TYPES
// (backend/tests/test_correction_types_parity.py keeps them equal).
// One issue, one form: each type only shows the inputs it needs.

export const MAX_TEXT = 500;
export const ESCALATE_AT = 3;

export const ISSUE_TYPES = {
  asr_error: {
    role: 'user', requires: 'spans_with_right',
    zh: '语音识别错误', en: 'Speech recognised wrongly',
    hintZh: '用鼠标选中听错的字，标红后在右边填写客户实际说的内容。', hintEn: 'Select the misheard words, then type what the customer actually said.',
  },
  user_other: {
    role: 'user', requires: 'expected_or_note',
    zh: '其他（客户这句话）', en: 'Other (customer sentence)',
    hintZh: '写明具体问题和正确理解。', hintEn: 'Describe the problem and the correct understanding.',
  },
  product_missed: {
    role: 'agent', requires: 'products',
    zh: '商品未检出', en: 'Product not found',
    hintZh: '商品库里有这个商品，AI 没查出来。从商品库勾选正确的商品。', hintEn: 'The product exists but the AI did not find it. Pick it from the catalog.',
  },
  fake_product: {
    role: 'agent', requires: 'spans',
    zh: '虚假商品', en: 'Made-up product',
    hintZh: 'AI 说出了商品库里没有的商品。选中回答里虚构的商品名。', hintEn: 'The AI named a product that is not in the catalog. Select the made-up name.',
  },
  wrong_recommendation: {
    role: 'agent', requires: 'products',
    zh: '推荐错误', en: 'Wrong recommendation',
    hintZh: '推荐的商品存在，但不是客户要的。可选中推荐错的部分，并勾选应该推荐的商品。', hintEn: 'The product exists but is not what the customer wanted. Pick the right one.',
  },
  missing_link: {
    role: 'agent', requires: 'products',
    zh: '未发送商品链接', en: 'Product link not sent',
    hintZh: '应该给客户发商品链接但没有发。勾选应发送的商品。', hintEn: 'The AI should have offered a product link. Pick the product.',
  },
  missing_ticket: {
    role: 'agent', requires: null,
    zh: '未生成工单', en: 'No ticket created',
    hintZh: 'AI 处理不了，却没有为客户升级工单。可补充说明。', hintEn: 'The AI could not help and did not escalate a ticket.',
  },
  repeated_answer: {
    role: 'agent', requires: 'repeat_count',
    zh: '重复回答', en: 'Repeated answer',
    hintZh: `同一意思重复 ${ESCALATE_AT} 次及以上才算。填写重复次数，可选中重复的内容。`, hintEn: `Counts only when the same thing is said ${ESCALATE_AT}+ times.`,
  },
  brand_tone: {
    role: 'agent', requires: 'spans_with_right',
    zh: '品牌话术', en: 'Brand wording',
    hintZh: '回答没错，但说法不合品牌语气。选中原表达，填写希望的说法。', hintEn: 'Correct but off-brand. Select the wording and type the preferred one.',
  },
  policy: {
    role: 'agent', requires: 'policy_or_expected',
    zh: '价格与政策', en: 'Price or policy',
    hintZh: '到货时间、退换货、价格等回答错误。勾选对应的 QA，或填写正确答案。', hintEn: 'Wrong delivery, returns or price answer. Pick the QA entry or type the right answer.',
  },
  agent_other: {
    role: 'agent', requires: 'expected_or_note',
    zh: '其他（AI 回答）', en: 'Other (AI answer)',
    hintZh: '以上都不是时使用，写明具体问题和正确做法。', hintEn: 'Use only when nothing above fits.',
  },
};

export const typesForRole = (role) => Object.entries(ISSUE_TYPES).filter(([, spec]) => spec.role === role).map(([key]) => key);

export const typeLabel = (type, lang = 'zh') => ISSUE_TYPES[type]?.[lang === 'zh' ? 'zh' : 'en'] || type;

export const emptyIssue = (type) => ({ type, spans: [], products: [], policies: [], expected: '', note: '', repeat_count: type === 'repeated_answer' ? ESCALATE_AT : undefined });

const blank = (value) => !String(value || '').trim();

// Same rules as the backend; returns a message key or null.
export const issueProblem = (issue, lang = 'zh') => {
  const zh = lang === 'zh';
  const spec = ISSUE_TYPES[issue?.type];
  if (!spec) return zh ? '请选择问题类型' : 'Choose a problem type';
  const texts = [issue.expected, issue.note, ...(issue.spans || []).map((s) => s.right)];
  if (texts.some((t) => String(t || '').length > MAX_TEXT)) return zh ? `每项最多 ${MAX_TEXT} 字` : `At most ${MAX_TEXT} characters each`;
  switch (spec.requires) {
    case 'spans_with_right':
      if (!issue.spans?.length) return zh ? '请先选中原文中有问题的字' : 'Select the words first';
      if (issue.spans.some((s) => blank(s.right))) return zh ? '请为每处标红填写正确内容' : 'Type the right words for each mark';
      return null;
    case 'spans':
      return issue.spans?.length ? null : (zh ? '请选中回答中虚构的商品' : 'Select the made-up product');
    case 'products':
      return issue.products?.length ? null : (zh ? '请从商品库勾选商品' : 'Pick a product');
    case 'repeat_count':
      return (issue.repeat_count || 0) >= ESCALATE_AT ? null : (zh ? `重复 ${ESCALATE_AT} 次及以上才算` : `Needs ${ESCALATE_AT}+ repeats`);
    case 'policy_or_expected':
      return issue.policies?.length || !blank(issue.expected) ? null : (zh ? '请勾选政策或填写正确答案' : 'Pick a policy or type the answer');
    case 'expected_or_note':
      return !blank(issue.expected) || !blank(issue.note) ? null : (zh ? '请写明具体问题' : 'Describe the problem');
    default:
      return null;
  }
};

// Payload the API accepts: drop empty fields.
export const cleanIssue = (issue) => {
  const out = { type: issue.type };
  if (issue.spans?.length) out.spans = issue.spans.map(({ start, end, wrong, right }) => (right ? { start, end, wrong, right } : { start, end, wrong }));
  if (issue.products?.length) out.products = issue.products.map(({ id, name }) => ({ id: String(id), name }));
  if (issue.policies?.length) out.policies = issue.policies.map(({ id, question }) => ({ id: String(id), question }));
  if (!blank(issue.expected)) out.expected = issue.expected.trim();
  if (!blank(issue.note)) out.note = issue.note.trim();
  if (issue.type === 'repeated_answer') out.repeat_count = Number(issue.repeat_count) || 0;
  return out;
};

// Add a span for [start, end) of text unless it overlaps an existing one.
export const addSpan = (spans, text, start, end) => {
  const s = Math.max(0, Math.min(start, end));
  const e = Math.min(text.length, Math.max(start, end));
  if (e <= s || !text.slice(s, e).trim()) return spans;
  if ((spans || []).some((span) => s < span.end && e > span.start)) return spans;
  return [...(spans || []), { start: s, end: e, wrong: text.slice(s, e), right: '' }].sort((a, b) => a.start - b.start);
};

// Text split into plain and marked pieces, for rendering marks in red.
export const splitBySpans = (text, spans) => {
  const pieces = [];
  let at = 0;
  [...(spans || [])].sort((a, b) => a.start - b.start).forEach((span, index) => {
    if (span.start > at) pieces.push({ text: text.slice(at, span.start) });
    pieces.push({ text: text.slice(span.start, span.end), span, index });
    at = span.end;
  });
  if (at < text.length) pieces.push({ text: text.slice(at) });
  return pieces;
};

// "原文【错 → 对】" summary used in lists.
export const describeIssue = (issue, lang = 'zh') => {
  const parts = [];
  (issue.spans || []).forEach((s) => parts.push(s.right ? `【${s.wrong} → ${s.right}】` : `【${s.wrong}】`));
  if (issue.products?.length) parts.push(issue.products.map((p) => p.name).join('、'));
  if (issue.policies?.length) parts.push(issue.policies.map((p) => p.question).join('、'));
  if (issue.repeat_count) parts.push(lang === 'zh' ? `重复 ${issue.repeat_count} 次` : `${issue.repeat_count} repeats`);
  if (issue.expected) parts.push(issue.expected);
  if (issue.note) parts.push(issue.note);
  return parts.join(' · ');
};

// Caller and AI sentences of one record, each correctable (checklist 14-17).
export const correctableTurns = (messages) => (messages || [])
  .map((m, index) => ({ ...m, index, role: m.role === 'assistant' ? 'agent' : m.role }))
  .filter((m) => (m.role === 'user' || m.role === 'agent') && String(m.content || '').trim());

export const turnKey = (turn) => turn.turn_id || String(turn.index);
